"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useId } from 'react';
import { validateSurface } from '../../globals.js';
import getOS from '../../utilities/check-os/index.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import getNodeText from '../../utilities/get-node-text/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import '../../utilities/use-viewport/index.js';
import styles from './radio-box.module.scss.js';

/**
 * @component RadioBox
 * @package Radios
 * @document ../_docs/code/RadioBox.editor.tsx
 * @document ../_docs/code/RadioBox.snippet.tsx
 * @document ../_docs/code/RadioBox.import.tsx
 */
const RadioBox = forwardRef((_a, ref) => {
    var { children, name, value, label, sublabel, sublabelRight, onChange, outOfStock, disabled, selected, surface: surfaceProp = 'lightPrimary', padding, width, height, id, inputId, ariaLabel: ariaLabelProp, ariaDescribedBy, 'aria-label': arialabel, 'data-testid': testId = 'radio-box', 'data-track': track, 'data-track-ignore': trackIgnore, 'data-analyticstrack': analyticsTrack, 'data-clickstream': clickStream } = _a, rest = __rest(_a, ["children", "name", "value", "label", "sublabel", "sublabelRight", "onChange", "outOfStock", "disabled", "selected", "surface", "padding", "width", "height", "id", "inputId", "ariaLabel", "ariaDescribedBy", 'aria-label', 'data-testid', 'data-track', 'data-track-ignore', 'data-analyticstrack', 'data-clickstream']);
    const radioId = useRef(null);
    const _useId = useId();
    if (!radioId.current)
        radioId.current = inputId || _useId;
    const surface = validateSurface(surfaceProp);
    const isAndroidDevice = getOS() === 'android';
    const calculateAriaLabel = () => {
        const ariaLabelValue = ariaLabelProp || arialabel || '';
        /* istanbul ignore else -- @preserve */
        if (ariaLabelValue)
            return outOfStock ? `${ariaLabelValue} out of stock` : ariaLabelValue;
        /* istanbul ignore else -- @preserve */
        if (children && typeof getNodeText(children) === 'string')
            return getNodeText(children);
        return [label, sublabel, sublabelRight].filter(Boolean).join(' ') || '';
    };
    const radioBoxContainerStyles = [
        styles.radioBoxItem,
        styles[surface.toLowerCase()],
        disabled ? styles.disabled : undefined,
        selected ? styles.selected : undefined,
    ];
    const labelContainer = [
        styles.labelContainer,
        sublabel || sublabelRight ? styles.bold : undefined,
    ];
    const _onChange = (e) => {
        /* istanbul ignore else -- @preserve */
        if (onChange)
            onChange(e);
    };
    const _onKeyDown = (e) => {
        /* istanbul ignore else -- @preserve */
        if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.click();
        }
    };
    const findWidth = () => calculateRem(width);
    return (jsxs("div", { id: id, className: radioBoxContainerStyles.join(' '), style: {
            flex: width ? `0 0 ${findWidth()}` : undefined,
            height: height || undefined,
            width: width ? findWidth() : undefined,
        }, children: [outOfStock && (jsx("svg", { className: styles.strikeThroughWrapper, children: jsx("line", { x1: "0", y1: "100%", x2: "100%", y2: "0", className: styles.outOfStockLine }) })), jsxs("div", Object.assign({ className: styles.radioBoxContentContainer }, (height && {
                style: {
                    height,
                },
            }), { children: [jsx("input", Object.assign({ type: "radio", "aria-checked": selected, "aria-label": calculateAriaLabel(), "aria-describedby": ariaDescribedBy, checked: !disabled && selected, disabled: disabled, value: value, name: name, onChange: _onChange, onKeyDown: _onKeyDown, tabIndex: disabled ? -1 : 0, "data-track": track, "data-track-ignore": trackIgnore, "data-analyticstrack": analyticsTrack, "data-clickstream": clickStream, ref: ref, id: radioId.current, "data-testid": testId }, rest)), jsx("label", { htmlFor: radioId.current, className: labelContainer.join(' '), "aria-hidden": isAndroidDevice || false, "data-testid": `${testId}-label`, style: {
                            padding: padding || `var(--vds-space-3x)`,
                        }, children: jsxs("span", { id: `label-${radioId.current}`, className: styles.flexWrapper, "aria-hidden": true, children: [jsxs("span", { className: styles.childWrapper, children: [label && !children && (jsx("span", { className: styles.labelContent, children: label })), sublabel && !children && (jsx("span", { className: styles.subLabel, children: sublabel })), children && children, outOfStock && (jsx(ScreenReaderText, { children: "Out of stock" }))] }), sublabelRight && !children && (jsx("span", { className: styles.subLabelRight, children: sublabelRight }))] }) })] }))] }));
});

export { RadioBox, RadioBox as default };
